import { useMemo } from 'react';
import type { TarotReading } from '../types/tarot';
import type { SearchFilters } from '../components/history/AdvancedSearchFilters';
import { spreads } from '../data/spreads';

export function useAdvancedReadingFilters(
  readings: TarotReading[],
  filters: SearchFilters
) {
  const filteredAndSortedReadings = useMemo(() => {
    let filtered = [...readings];

    // Text search - search in question, interpretation, and AI interpretation
    if (filters.query.trim()) {
      const query = filters.query.toLowerCase().trim();
      filtered = filtered.filter(reading => {
        return (
          reading.question.toLowerCase().includes(query) ||
          reading.interpretation?.toLowerCase().includes(query) ||
          reading.aiInterpretation?.toLowerCase().includes(query) ||
          reading.tags.some(tag => tag.toLowerCase().includes(query))
        );
      });
    }

    // Date range filter
    if (filters.dateRange.start) {
      const startDate = new Date(filters.dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(reading => 
        new Date(reading.createdAt) >= startDate
      );
    }

    if (filters.dateRange.end) {
      const endDate = new Date(filters.dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(reading => 
        new Date(reading.createdAt) <= endDate
      );
    }

    // Spread type filter
    if (filters.spreadTypes.length > 0) {
      filtered = filtered.filter(reading => 
        filters.spreadTypes.includes(reading.spreadId)
      );
    }

    // Favorite filter
    if (filters.favoriteOnly) {
      filtered = filtered.filter(reading => reading.isFavorite);
    }

    // Private filter
    if (filters.privateOnly) {
      filtered = filtered.filter(reading => reading.isPrivate);
    }

    // Tags filter
    if (filters.tags.length > 0) {
      filtered = filtered.filter(reading => 
        filters.tags.every(tag => reading.tags.includes(tag))
      );
    }

    // Sort the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case 'newest':
        case 'oldest':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          if (filters.sortBy === 'newest') comparison = -comparison;
          break;

        case 'favorites':
          // Favorites first, then by date
          if (a.isFavorite && !b.isFavorite) comparison = -1;
          else if (!a.isFavorite && b.isFavorite) comparison = 1;
          else comparison = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          break;

        case 'spread':
          const spreadA = spreads.find(s => s.id === a.spreadId)?.name || '';
          const spreadB = spreads.find(s => s.id === b.spreadId)?.name || '';
          comparison = spreadA.localeCompare(spreadB);
          break;

        case 'question':
          comparison = a.question.localeCompare(b.question);
          break;

        default:
          comparison = 0;
      }

      // Apply sort order
      if (filters.sortOrder === 'asc' && filters.sortBy !== 'favorites') {
        comparison = -comparison;
      }

      return comparison;
    });

    return filtered;
  }, [readings, filters]);

  // Extract available tags from all readings
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    readings.forEach(reading => {
      reading.tags.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [readings]);

  // Calculate filter statistics
  const filterStats = useMemo(() => {
    return {
      total: readings.length,
      filtered: filteredAndSortedReadings.length,
      hasActiveFilters: 
        filters.query.trim() !== '' ||
        filters.dateRange.start !== '' ||
        filters.dateRange.end !== '' ||
        filters.spreadTypes.length > 0 ||
        filters.favoriteOnly ||
        filters.privateOnly ||
        filters.tags.length > 0 ||
        filters.sortBy !== 'newest'
    };
  }, [readings.length, filteredAndSortedReadings.length, filters]);

  return {
    filteredReadings: filteredAndSortedReadings,
    availableTags,
    filterStats
  };
}